import React, { useCallback, useEffect, useState } from "react";
import _ from "lodash";
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  useMantineTheme,
  Button,
  Center,
  Drawer,
  Stack,
  Text,
  Card,
  ActionIcon,
} from "@mantine/core";
import {
  IconPlus,
  IconClock,
  IconCalendar,
  IconChevronRight,
  IconRobot,
  IconWorld,
  IconAdjustments,
  IconGitBranch,
  IconCode,
  IconUserCheck,
  IconCirclePlus,
  IconNetwork,
  IconMail,
  IconPlayerPlay,
} from "@tabler/icons";

import { Accordion, Group, Badge, Code, Tabs } from "@mantine/core";
import { useServerApi } from "../../hooks/userServerApi";
import moment from "moment";

const initialPosition = { x: 30, y: 20 };

const WorkflowTester = ({ form, ...props }) => {
  const [api] = useServerApi();

  //test results
  const [testResults, setTestResults] = useState([]);
  const [isRunning, setIsRunning] = useState(false);

  //test button
  const handleTest = async () => {
    try {
      // console.log("test", form.values._id);
      setIsRunning(true);
      if (!form.values._id) return;
      setTestResults([]);
      const data = await api.AIWorkFlow.runWorkflow(form.values._id);
      // console.log("data", data);
      setTestResults(data.result);
      setIsRunning(false);
    } catch (error) {
      console.error(error);
      setIsRunning(false);
    }
  };

  // Helper function to render loop results
  const renderLoopResults = (result) => {
    const { loopResults, totalItems } = result.output;

    return (
      <Tabs defaultValue="summary">
        <Tabs.List>
          <Tabs.Tab value="summary">Summary</Tabs.Tab>
          {loopResults?.map((_, index) => (
            <Tabs.Tab key={index} value={`iteration-${index}`}>
              Iteration {index + 1}
            </Tabs.Tab>
          ))}
        </Tabs.List>

        <Tabs.Panel value="summary" pt="md">
          <Card withBorder>
            <Stack spacing="xs">
              <Text size="sm" fw={500}>
                Loop Summary
              </Text>
              <Text size="xs">Total Items: {totalItems}</Text>
              <Text size="xs">
                Iterations Completed: {loopResults?.length || 0}
              </Text>
              <Text size="xs">
                Success Rate:{" "}
                {loopResults?.filter((r) => r.success).length || 0}/
                {loopResults?.length || 0}
              </Text>
              {result.timestamp && (
                <Text size="xs">
                  Started:{" "}
                  {moment(result.timestamp).format("YYYY-MM-DD HH:mm:ss")}
                </Text>
              )}
              {result.executionTime && (
                <Text size="xs">
                  Total Execution Time: {result.executionTime}ms
                </Text>
              )}
            </Stack>
          </Card>
        </Tabs.Panel>

        {loopResults?.map((iterationResult, index) => (
          <Tabs.Panel key={index} value={`iteration-${index}`} pt="md">
            <Stack spacing="xs">
              <Group>
                <Badge
                  color={iterationResult.success ? "green" : "red"}
                  size="xs"
                  radius={0}
                >
                  {iterationResult.success ? "Success" : "Failed"}
                </Badge>
                <Text size="sm" fw={500}>
                  Iteration {iterationResult.iteration}
                </Text>
                {iterationResult.timestamp && (
                  <Text size="xs" c="dimmed">
                    {moment(iterationResult.timestamp).format("HH:mm:ss")}
                  </Text>
                )}
                {iterationResult.executionTime && (
                  <Text size="xs" c="dimmed">
                    ({iterationResult.executionTime}ms)
                  </Text>
                )}
              </Group>

              {/* Show all nodes executed in this iteration */}
              <Stack spacing="md">
                {iterationResult.nodeResults?.map((nodeResult, nodeIndex) => (
                  <Card key={nodeIndex} withBorder>
                    <Stack spacing="xs">
                      <Group>
                        <Badge
                          color={nodeResult.success ? "green" : "red"}
                          size="xs"
                          radius={0}
                        >
                          {nodeResult.success ? "Success" : "Failed"}
                        </Badge>
                        <Text size="sm" fw={500}>
                          {nodeResult.nodeLabel}
                        </Text>
                        {nodeResult.timestamp && (
                          <Text size="xs" c="dimmed">
                            {moment(nodeResult.timestamp).format("HH:mm:ss")}
                          </Text>
                        )}
                        {nodeResult.executionTime && (
                          <Text size="xs" c="dimmed">
                            ({nodeResult.executionTime}ms)
                          </Text>
                        )}
                      </Group>

                      <Tabs defaultValue="output">
                        <Tabs.List>
                          <Tabs.Tab value="output">Output</Tabs.Tab>
                          <Tabs.Tab value="logs">Logs</Tabs.Tab>
                          {nodeResult.errors?.length > 0 && (
                            <Tabs.Tab value="errors">Errors</Tabs.Tab>
                          )}
                        </Tabs.List>

                        <Tabs.Panel value="output" pt="md">
                          {/* Check if this node result has nested loop results */}
                          {nodeResult.output?.loopResults ? (
                            <div>
                              <Text size="xs" c="blue" mb="sm">
                                🔄 Nested Loop Detected -{" "}
                                {nodeResult.output.loopResults.length}{" "}
                                iterations
                              </Text>
                              {renderLoopResults(nodeResult)}
                            </div>
                          ) : (
                            <Card
                              withBorder
                              style={{ maxHeight: "400px", overflow: "auto" }}
                            >
                              <Text
                                size="xs"
                                style={{ whiteSpace: "pre-wrap" }}
                              >
                                {JSON.stringify(nodeResult.output, null, 2)}
                              </Text>
                            </Card>
                          )}
                        </Tabs.Panel>

                        <Tabs.Panel value="logs" pt="md">
                          <div style={{ maxHeight: "300px", overflow: "auto" }}>
                            <Stack spacing="xs">
                              {!_.isEmpty(nodeResult.logs) ? (
                                nodeResult.logs.map((log, logIndex) => (
                                  <Card key={logIndex} withBorder>
                                    <Group spacing="xs" align="flex-start">
                                      <Text
                                        size="xs"
                                        c="dimmed"
                                        style={{ minWidth: "150px" }}
                                      >
                                        {moment(log?.timestamp).format(
                                          "YYYY-MM-DD HH:mm:ss"
                                        )}
                                      </Text>
                                      <Text size="xs">{log?.message}</Text>
                                    </Group>
                                  </Card>
                                ))
                              ) : (
                                <Card withBorder>
                                  <Text size="xs" c="dimmed">
                                    No logs found for this node
                                  </Text>
                                </Card>
                              )}
                            </Stack>
                          </div>
                        </Tabs.Panel>

                        {nodeResult.errors?.length > 0 && (
                          <Tabs.Panel value="errors" pt="md">
                            <Stack spacing="xs">
                              {nodeResult.errors.map((error, errorIndex) => (
                                <Card key={errorIndex} withBorder>
                                  <Text size="xs" c="red">
                                    {error.message}
                                  </Text>
                                </Card>
                              ))}
                            </Stack>
                          </Tabs.Panel>
                        )}
                      </Tabs>
                    </Stack>
                  </Card>
                ))}
              </Stack>
            </Stack>
          </Tabs.Panel>
        ))}
      </Tabs>
    );
  };

  // Helper function to render regular node results
  const renderRegularResults = (result) => {
    return (
      <Tabs defaultValue="output">
        <Tabs.List>
          <Tabs.Tab value="output">Output</Tabs.Tab>
          <Tabs.Tab value="logs">Logs</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="logs">
          <div style={{ maxHeight: "400px", overflow: "auto" }}>
            <Stack spacing="xs" pt="xl">
              {!_.isEmpty(result.logs) &&
                result.logs?.map((log, index) => (
                  <Card key={index} withBorder>
                    <Group spacing="xs" align="flex-start">
                      <Text size="xs" c="dimmed" style={{ minWidth: "150px" }}>
                        {moment(log?.timestamp).format("YYYY-MM-DD HH:mm:ss")}
                      </Text>
                      <Text size="xs">{log?.message}</Text>
                    </Group>
                  </Card>
                ))}
              {_.isEmpty(result.logs) && (
                <Card withBorder>
                  <Text size="xs" c="dimmed" style={{ minWidth: "150px" }}>
                    No logs found
                  </Text>
                </Card>
              )}
            </Stack>
          </div>
        </Tabs.Panel>

        <Tabs.Panel value="output">
          <Stack spacing="xs" pt="xl">
            {result.output ? (
              <Card
                withBorder
                style={{
                  maxHeight: "500px",
                  overflow: "auto",
                }}
              >
                <Text
                  size="xs"
                  style={{ whiteSpace: "pre-wrap", fontFamily: "monospace" }}
                >
                  {JSON.stringify(result.output, null, 2)}
                </Text>
              </Card>
            ) : (
              <Card withBorder>
                <Text size="xs" c="dimmed">
                  No output found
                </Text>
              </Card>
            )}
          </Stack>
        </Tabs.Panel>
      </Tabs>
    );
  };

  return (
    <>
      <Button
        onClick={async () => await handleTest()}
        size="xs"
        leftIcon={<IconPlayerPlay size={14} />}
        loading={isRunning}
      >
        Run Workflow
      </Button>

      <Stack spacing="md" mt="md">
        <Accordion>
          {testResults.map((result, index) => (
            <Accordion.Item
              value={`${index}-${result.nodeId}`}
              key={index}
              mb="md"
              size="xs"
            >
              <Accordion.Control>
                <Group>
                  <Badge
                    color={result.success ? "green" : "red"}
                    size="xs"
                    radius={0}
                  >
                    {result.success ? "Success" : "Failed"}
                  </Badge>
                  <Text size="sm" fw={500}>
                    {result.nodeLabel}
                  </Text>
                  {result.output?.loopResults && (
                    <Badge color="blue" size="xs" radius={0}>
                      Loop ({result.output.totalItems} items)
                    </Badge>
                  )}
                  {result.timestamp && (
                    <Text size="xs" c="dimmed">
                      {moment(result.timestamp).format("HH:mm:ss")}
                    </Text>
                  )}
                  {result.executionTime && (
                    <Text size="xs" c="dimmed">
                      ({result.executionTime}ms)
                    </Text>
                  )}
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                {result.output?.loopResults ? (
                  <div>
                    <Text size="xs" c="green" mb="sm">
                      🔄 Main Loop - {result.output.loopResults.length}{" "}
                      iterations
                    </Text>
                    {renderLoopResults(result)}
                  </div>
                ) : (
                  renderRegularResults(result)
                )}
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      </Stack>
    </>
  );
};

export default WorkflowTester;
